import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  Location,
  NavigateOptions,
  To,
  useBlocker,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useFeedback } from '../feedback/FeedbackProvider';

let runtime = Math.random().toString(36).slice(2);
const field = '__qkNavigation';
const confirmationKey = 'feedback.confirmation';
let confirmationSequence = 0;
type Values = Record<string, unknown>;
interface Entry {
  runtime: string;
  values: Values;
  previous?: string;
}
export interface NavigationGuard {
  scopeKey?: string;
  dirty: boolean;
  pending: boolean;
  discard: () => void;
}
interface NavigationApi {
  location: Location;
  values: Values;
  setState: (
    key: string,
    value: unknown,
    initial: unknown,
    replace?: boolean,
  ) => void;
  replaceState: (key: string, value: unknown) => void;
  reset: () => void;
  requestAction: (action: () => void | Promise<void>) => Promise<boolean>;
  requestTransition: (
    patch: Values,
    prepare?: () => void,
    options?: { replace?: boolean },
  ) => Promise<boolean>;
  updateStates: (patch: Values, options?: { replace?: boolean }) => void;
  goBack: (fallback?: string) => void;
  registerDefault: (key: string, value: unknown) => void;
  navigate: (to: To, options?: NavigateOptions) => void;
  registerGuard: (id: symbol, guard: () => NavigationGuard) => () => void;
  allowClose: (id: symbol) => void;
}
const Context = createContext<NavigationApi | null>(null);
const fingerprint = (values: Values) =>
  JSON.stringify(
    Object.keys(values)
      .sort()
      .map((key) => [key, values[key]]),
  );
const signature = (location: Location, values: Values) =>
  JSON.stringify([
    location.pathname,
    location.search,
    location.hash,
    fingerprint(values),
  ]);
function plainObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}
function safeKey(key: string) {
  return (
    key.length > 0 &&
    key.length <= 200 &&
    !['__proto__', 'prototype', 'constructor'].includes(key)
  );
}
function scalar(value: unknown): boolean {
  return (
    value == null ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value)) ||
    (typeof value === 'string' &&
      value.length <= 512 &&
      !/^(data:|blob:)/i.test(value))
  );
}
function validValues(values: unknown): values is Values {
  if (!plainObject(values) || Object.keys(values).length > 40) return false;
  const valid = Object.entries(values).every(
    ([key, value]) =>
      safeKey(key) &&
      (scalar(value) ||
        (plainObject(value) &&
          Object.keys(value).length <= 16 &&
          Object.entries(value).every(
            ([name, item]) => safeKey(name) && scalar(item),
          ))),
  );
  return valid && JSON.stringify(values).length <= 16000;
}
function validEntry(entry: unknown): entry is Entry {
  return (
    plainObject(entry) &&
    entry.runtime === runtime &&
    validValues(entry.values) &&
    (entry.previous === undefined ||
      (typeof entry.previous === 'string' && entry.previous.length <= 24000))
  );
}
function assertValues(values: Values) {
  if (!validValues(values))
    throw new Error(
      'Navigation state must contain only bounded, flat presentation descriptors and identifiers.',
    );
}
function read(location: Location): Entry {
  const entry: unknown = location.state?.[field];
  return validEntry(entry) ? entry : { runtime, values: {} };
}

/** Router location is the sole owner of presentation state. Payloads belong in feature sessions. */
export function NavigationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    confirmDiscardChanges,
    cancelConfirmation,
    hasConfirmation,
    hasNavigableConfirmation,
    registerConfirmationNavigation,
  } = useFeedback();
  const entry = read(location);
  const current = useRef({ location, entry });
  current.current = { location, entry };
  const defaults = useRef<Values>({});
  const guards = useRef(new Map<symbol, () => NavigationGuard>());
  const allowed = useRef(new Set<symbol>());
  const affected = useRef<NavigationGuard[]>([]);
  const confirming = useRef(false);
  const closingConfirmation = useRef<{
    key: string;
    resolve: () => void;
  } | null>(null);
  const actionRunning = useRef(false);
  const generation = useRef(0);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (confirming.current) return true;
    const next = read(nextLocation).values;
    const previous = read(currentLocation).values;
    const leavingPage =
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search;
    affected.current = [...guards.current.entries()]
      .filter(([id, get]) => {
        const guard = get();
        return (
          !allowed.current.has(id) &&
          (guard.dirty || guard.pending) &&
          (leavingPage ||
            (guard.scopeKey
              ? JSON.stringify(previous[guard.scopeKey]) !==
                JSON.stringify(next[guard.scopeKey])
              : fingerprint(previous) !== fingerprint(next)))
        );
      })
      .map(([, get]) => get());
    allowed.current.clear();
    return affected.current.length > 0;
  });
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useEffect(() => {
    if (blocker.state !== 'blocked' || confirming.current) return;
    if (affected.current.some((guard) => guard.pending)) {
      blocker.reset();
      return;
    }
    confirming.current = true;
    const leaving = affected.current;
    const started = generation.current;
    void confirmDiscardChanges()
      .then((discard) => {
        if (started !== generation.current) return;
        // Resume the first requested transition, even if Back is pressed again while confirming.
        if (discard) {
          leaving.forEach((guard) => guard.discard());
          blocker.proceed();
        } else blocker.reset();
      })
      .finally(() => {
        if (started === generation.current) confirming.current = false;
      });
  }, [blocker, confirmDiscardChanges, cancelConfirmation]);
  useEffect(() => {
    // Restored browser history cannot resurrect camera/editor sessions after a reload.
    if (location.state?.[field] && !validEntry(location.state[field])) {
      const state = { ...location.state };
      delete state[field];
      navigate(location.pathname + location.search + location.hash, {
        replace: true,
        state,
      });
    }
  }, [location, navigate]);
  const setState = useCallback(
    (key: string, value: unknown, initial: unknown, replace = false) => {
      const { location: from, entry: old } = current.current;
      const closing = JSON.stringify(value) === JSON.stringify(initial);
      const values = { ...old.values };
      if (closing) delete values[key];
      else values[key] = value;
      if (fingerprint(values) === fingerprint(old.values)) return;
      assertValues(values);
      if (!replace && closing && old.previous === signature(from, values)) {
        navigate(-1);
        return;
      }
      const next: Entry = {
        runtime,
        values,
        previous:
          replace || closing ? old.previous : signature(from, old.values),
      };
      current.current = { location: from, entry: next };
      navigate(from.pathname + from.search + from.hash, {
        replace: replace || closing,
        state: { ...from.state, [field]: next },
      });
    },
    [navigate],
  );
  useEffect(
    () =>
      registerConfirmationNavigation({
        open: () =>
          setState(confirmationKey, ++confirmationSequence, undefined),
        close: () =>
          new Promise<void>((resolve) => {
            const from = current.current;
            if (from.entry.values[confirmationKey] === undefined) {
              resolve();
              return;
            }
            closingConfirmation.current = { key: from.location.key, resolve };
            setState(confirmationKey, undefined, undefined);
          }),
      }),
    [registerConfirmationNavigation, setState],
  );
  useEffect(() => {
    const shown = entry.values[confirmationKey] !== undefined;
    const closing = closingConfirmation.current;
    if (closing && closing.key !== location.key) {
      closingConfirmation.current = null;
      closing.resolve();
    }
    if (!shown && hasNavigableConfirmation())
      cancelConfirmation({ fromNavigation: true });
    else if (
      shown &&
      !hasNavigableConfirmation() &&
      !closingConfirmation.current
    ) {
      // Forward cannot replay a resolved operation or restore its private prompt payload.
      setState(confirmationKey, undefined, undefined, true);
    }
  }, [
    entry.values,
    location.key,
    hasNavigableConfirmation,
    cancelConfirmation,
    setState,
  ]);
  useEffect(
    () => () => {
      closingConfirmation.current?.resolve();
    },
    [],
  );
  const reset = useCallback(() => {
    const { location: from } = current.current;
    generation.current += 1;
    confirming.current = false;
    cancelConfirmation({ fromNavigation: true });
    if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
    guards.current.forEach((_, id) => allowed.current.add(id));
    runtime = Math.random().toString(36).slice(2);
    const state = { ...from.state };
    delete state[field];
    navigate(from.pathname + from.search + from.hash, { replace: true, state });
  }, [navigate, cancelConfirmation]);
  const requestAction = useCallback(
    async (
      action: () => void | Promise<void>,
      scopes?: string[],
    ): Promise<boolean> => {
      if (confirming.current || actionRunning.current || hasConfirmation())
        return false;
      const active = [...guards.current.entries()]
        .map(([id, get]) => ({ id, guard: get() }))
        .filter(
          ({ guard }) =>
            !scopes || !guard.scopeKey || scopes.includes(guard.scopeKey),
        );
      if (active.some(({ guard }) => guard.pending)) return false;
      actionRunning.current = true;
      const started = generation.current;
      try {
        const dirty = active.filter(({ guard }) => guard.dirty);
        if (dirty.length) {
          confirming.current = true;
          const accepted = await confirmDiscardChanges();
          confirming.current = false;
          if (blockerRef.current.state === 'blocked')
            blockerRef.current.reset();
          if (
            !accepted ||
            started !== generation.current ||
            active.some(({ id }) => guards.current.get(id)?.().pending)
          )
            return false;
          dirty.forEach(({ guard }) => guard.discard());
        }
        active.forEach(({ id }) => allowed.current.add(id));
        const result = action();
        if (result) await result;
        return true;
      } finally {
        confirming.current = false;
        actionRunning.current = false;
        allowed.current.clear();
      }
    },
    [confirmDiscardChanges, hasConfirmation],
  );
  const registerGuard = useCallback(
    (id: symbol, get: () => NavigationGuard) => {
      guards.current.set(id, get);
      return () => {
        guards.current.delete(id);
        allowed.current.delete(id);
      };
    },
    [],
  );
  const allowClose = useCallback((id: symbol) => {
    allowed.current.add(id);
  }, []);
  const registerDefault = useCallback((key: string, value: unknown) => {
    defaults.current[key] = value;
  }, []);
  const updateStates = useCallback(
    (patch: Values, options?: { replace?: boolean }) => {
      const { location: from, entry: old } = current.current;
      const values = { ...old.values };
      Object.entries(patch).forEach(([key, value]) => {
        if (JSON.stringify(value) === JSON.stringify(defaults.current[key]))
          delete values[key];
        else values[key] = value;
      });
      if (fingerprint(values) === fingerprint(old.values)) return;
      assertValues(values);
      const next: Entry = {
        runtime,
        values,
        previous: options?.replace ? old.previous : signature(from, old.values),
      };
      current.current = { location: from, entry: next };
      navigate(from.pathname + from.search + from.hash, {
        replace: options?.replace,
        state: { ...from.state, [field]: next },
      });
    },
    [navigate],
  );
  // Prepare private draft data only after the affected guards accept. Commit all
  // descriptors in one PUSH/REPLACE; never combine an asynchronous POP with PUSH.
  const requestTransition = useCallback(
    (patch: Values, prepare?: () => void, options?: { replace?: boolean }) =>
      requestAction(() => {
        prepare?.();
        updateStates(patch, options);
      }, Object.keys(patch)),
    [requestAction, updateStates],
  );
  const replaceState = useCallback(
    (key: string, value: unknown) =>
      setState(key, value, defaults.current[key], true),
    [setState],
  );
  const navigatePage = useCallback(
    (to: To, options?: NavigateOptions) => {
      const from = current.current.location;
      navigate(to, {
        ...options,
        state: {
          ...options?.state,
          __qkPredecessor: options?.replace
            ? current.current.entry.previous !== undefined
              ? runtime
              : from.state?.__qkPredecessor
            : runtime,
        },
      });
    },
    [navigate],
  );
  const goBack = useCallback(
    (fallback = '/items') => {
      const { location: from, entry: old } = current.current;
      if (old.previous !== undefined || from.state?.__qkPredecessor === runtime)
        navigate(-1);
      else navigatePage(fallback, { replace: true });
    },
    [navigate, navigatePage],
  );
  return (
    <Context.Provider
      value={{
        location,
        values: entry.values,
        setState,
        replaceState,
        updateStates,
        registerDefault,
        goBack,
        reset,
        requestAction,
        requestTransition,
        navigate: navigatePage,
        registerGuard,
        allowClose,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useNavigation() {
  const context = useContext(Context);
  if (!context)
    throw new Error(
      'Navigation hooks require NavigationProvider inside a data router.',
    );
  return context;
}

export function useNavigationState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  const { values, setState, registerDefault } = useNavigation();
  registerDefault(key, initial);
  const value = Object.prototype.hasOwnProperty.call(values, key)
    ? (values[key] as T)
    : initial;
  const latest = useRef(value);
  latest.current = value;
  const initialRef = useRef(initial);
  const setter = useCallback<Dispatch<SetStateAction<T>>>(
    (update) => {
      const next =
        typeof update === 'function'
          ? (update as (previous: T) => T)(latest.current)
          : update;
      latest.current = next;
      setState(key, next, initialRef.current);
    },
    [key, setState],
  );
  return [value, setter];
}
